import { t } from "@lingui/macro"
import { Box, Button, Link } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useEffect, useState } from "react"

import { PropertyStore } from "./store"

import { AccessGroupsAdminService, ConnectorAdminService } from "src/api"
import { avy_api_pkg_common_IntegrationWithType } from "src/api/models/avy_api_pkg_common_IntegrationWithType"
import { Table } from "src/components/Table"
import { reportSuccess, reportUnhandledApiError } from "src/lib/report"
import { CreateNewPropertyModal } from "src/modals/create-new-property-modal"
import { FastApiPropertyLoaderModal } from "src/modals/fastapi-property-loader-modal"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { IPropertyOwner } from "src/views/client-management/store"

const View = observer(
    ({
        propertyOwner,
        legalEntityId,
        selectedLegalEntity,
        propertyOwnersList,
    }: IProps) => {
        const store = useStore(PropertyStore)
        const gstore = useStore(GlobalStore)
        const [hasFastApiIntegrations, setHasFastApiIntegrations] =
            useState(false)
        const [filteredFastApiConnectors, setFilteredFastApiConnectors] =
            useState<avy_api_pkg_common_IntegrationWithType[]>([])

        useEffect(() => {
            ;(async () => {
                await store.init(propertyOwner?.propertyOwnerId, legalEntityId)

                // Check if there are fastapi integrations available for this property owner
                try {
                    if (propertyOwner?.propertyOwnerId == null) {
                        setHasFastApiIntegrations(false)
                        setFilteredFastApiConnectors([])
                        return
                    }

                    // Load all access groups and fastapi connectors in parallel
                    const [accessGroups, fastApiConnectors] = await Promise.all(
                        [
                            AccessGroupsAdminService.getV1AdminAccessGroup(),
                            ConnectorAdminService.getV1AdminConnectorIntegration(
                                {
                                    type: "fastapi",
                                },
                            ),
                        ],
                    )

                    // Filter access groups that belong to the current property owner
                    const propertyOwnerAccessGroupIds = accessGroups
                        .filter(
                            (accessGroup) =>
                                accessGroup.property_owner_id ===
                                propertyOwner.propertyOwnerId,
                        )
                        .map((accessGroup) => accessGroup.id)
                        .filter((id): id is number => id !== undefined)

                    // Filter fastapi connectors that belong to the property owner's access groups
                    const filteredConnectors = fastApiConnectors.filter(
                        (connector) =>
                            propertyOwnerAccessGroupIds.includes(
                                connector.access_group_id,
                            ),
                    )

                    setHasFastApiIntegrations(filteredConnectors.length > 0)
                    setFilteredFastApiConnectors(filteredConnectors)
                } catch (error) {
                    setHasFastApiIntegrations(false)
                    setFilteredFastApiConnectors([])
                }
            })()
        }, [store, propertyOwner, legalEntityId])

        const propertyOwnerHasFastApiEnabled: boolean =
            propertyOwner?.integrationType === "fastapi"

        const handleLoadFastApiProperties = useCallback(() => {
            if (propertyOwner != null) {
                gstore.modals.open(
                    () => (
                        <FastApiPropertyLoaderModal
                            propertyOwner={propertyOwner}
                            existingProperties={store.property.items}
                            legalEntityId={legalEntityId}
                            legalEntityName={selectedLegalEntity?.label}
                            onPropertiesUpdated={() => store.property.reload()}
                            preFilteredConnectors={filteredFastApiConnectors}
                        />
                    ),
                    {
                        variant: "slide-up-w995",
                    },
                )
            }
        }, [
            gstore,
            propertyOwner,
            store.property,
            legalEntityId,
            selectedLegalEntity?.label,
            filteredFastApiConnectors,
        ])

        const handlePropertyFastApiClick = useCallback(
            (propertyOwnerId: number, propertyId: number, fullLoad: boolean) =>
                async () => {
                    try {
                        await store.propertyFastApiRequest(
                            propertyOwnerId,
                            propertyId,
                            fullLoad,
                        )
                        reportSuccess("The load was successful")
                    } catch (e) {
                        reportUnhandledApiError(e)
                    }
                },
            [store],
        )
        const handleOpenOnEditClick = useCallback(
            (item: number) => {
                gstore.modals.open(() => (
                    <CreateNewPropertyModal
                        id={item}
                        propertyOwnersList={propertyOwnersList}
                        chosenPropertyOwnerId={propertyOwner?.propertyOwnerId}
                    />
                ))
            },
            [gstore, propertyOwnersList, propertyOwner],
        )

        const handleOffboardProperty = useCallback(
            (propertyId: number) => async () => {
                try {
                    await store.offboardPropertyRequest(propertyId)
                    reportSuccess(t`property-view.offboard-success-message`)
                } catch (e) {
                    reportUnhandledApiError(e)
                }
            },
            [store],
        )

        const tableHeaders: {
            key:
                | "propertyId"
                | "name"
                | "city"
                | "externalId"
                | "integrationId"
                | "integrationActive"
                | "selfOnboardingEnabled"
                | "selfOnboardingCode"
                | "leaflet"
            name: string
            hidden?: boolean
        }[] = [
            {
                key: "propertyId",
                name: t`property-view.property-id-header`,
            },
            {
                key: "name",
                name: t`property-view.name-header`,
            },
            {
                key: "city",
                name: t`property-view.city-header`,
            },
            {
                key: "externalId",
                name: t`property-view.external-id-header`,
            },
            {
                key: "integrationId",
                name: t`property-view.integration-id-header`,
            },
            {
                key: "integrationActive",
                name: t`property-view.integration-active-header`,
            },
            {
                key: "selfOnboardingEnabled",
                name: t`property-view.self-onboarding-enabled-header`,
            },
            {
                key: "selfOnboardingCode",
                name: t`property-view.self-onboarding-code-header`,
            },
            {
                key: "leaflet",
                name: t`property-view.self-leaflet-header`,
            },
        ]

        const rowsRenderers = (item: {
            propertyId: number
            name: string
            city: string
            externalId: string
            integrationId: number | undefined
            integrationActive: boolean
            selfOnboardingEnabled: boolean
            selfOnboardingCode: number | string
            leaflet: string | null
        }) => ({
            propertyId: item.propertyId,
            name: (
                <Link
                    component="button"
                    onClick={() => handleOpenOnEditClick(item.propertyId)}
                    data-testid="edit-modal-button"
                    sx={{ textAlign: "left" }}
                >
                    {item.name}
                </Link>
            ),
            city: item.city,
            externalId: item.externalId,
            integrationId: item.integrationId,
            integrationActive:
                item.integrationActive === true
                    ? t`property-view.integration-active-true`
                    : t`property-view.integration-active-false`,
            selfOnboardingEnabled:
                item.selfOnboardingEnabled === true
                    ? t`property-view.self-onboarding-true`
                    : t`property-view.self-onboarding-false`,
            selfOnboardingCode: item.selfOnboardingCode,
            leaflet:
                item.leaflet != null && item.leaflet !== "" ? (
                    <Link href={item.leaflet.toString()} target="_blank">
                        {t`property-view.leaflet-link`}
                    </Link>
                ) : null,
        })

        const rowActionsRenderer = (item: {
            propertyOwnerId: number
            propertyId: number
        }) => {
            let rowActionOptions = [
                {
                    text: t`row-action-renderer.offboard-property`,
                    onClick: handleOffboardProperty(item.propertyId),
                },
            ]

            if (propertyOwnerHasFastApiEnabled) {
                rowActionOptions = rowActionOptions.concat([
                    {
                        text: t`row-action-renderer-fast-api.48h-load`,
                        onClick: handlePropertyFastApiClick(
                            item.propertyOwnerId,
                            item.propertyId,
                            false,
                        ),
                    },
                    {
                        text: t`row-action-renderer-fast-api.full-load`,
                        onClick: handlePropertyFastApiClick(
                            item.propertyOwnerId,
                            item.propertyId,
                            true,
                        ),
                    },
                ])
            }

            return rowActionOptions
        }

        return (
            <Box>
                {/* Show FastAPI property loader button if there are fastapi integrations available */}
                {hasFastApiIntegrations && propertyOwner && legalEntityId && (
                    <Box
                        sx={{
                            mb: 3,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleLoadFastApiProperties}
                            data-testid="load-fastapi-properties-button"
                        >
                            {t`property-view.load-property-data-from-fastapi`}
                        </Button>
                    </Box>
                )}

                <Table
                    paginator={store.property}
                    headers={tableHeaders}
                    rowRenderer={rowsRenderers}
                    rowActionsRenderer={rowActionsRenderer}
                />
            </Box>
        )
    },
)

export const PropertyTableView = ({
    propertyOwnersList,
    propertyOwner,
    legalEntityId,
    selectedLegalEntity,
    onEditClick,
}: IProps) => (
    <StoreProvider Store={PropertyStore}>
        <View
            propertyOwner={propertyOwner}
            legalEntityId={legalEntityId}
            selectedLegalEntity={selectedLegalEntity}
            propertyOwnersList={propertyOwnersList}
            onEditClick={onEditClick}
        />
    </StoreProvider>
)

interface IProps {
    propertyOwner?: IPropertyOwner
    legalEntityId?: number
    selectedLegalEntity?: { id: number; label: string } | null
    onEditClick: (item: {}) => void
    propertyOwnersList: IPropertyOwner[]
}
